// src/components/nftVerificationComponents/WalletManagement.js

import React, { useState, useContext } from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Add, Delete, InfoOutlined } from '@mui/icons-material';
import { useWallet } from '@solana/wallet-adapter-react';
import { AuthContext } from '../../context/AuthContext';
import axios from 'axios';
import { styled } from '@mui/system';

// Define color constants
const COLORS = {
  buttonGradient: 'linear-gradient(to right, #9AC0CD, #4C5C64)',
  buttonBoxShadow: '0 4px 15px rgba(154, 192, 205, 0.5)',
  buttonHoverBackground: '#4C5C64',
  buttonHoverBoxShadow: '0 4px 20px rgba(154, 192, 205, 0.7)',
  buttonActiveBackground: '#5A6C45',
  chipBackground: '#4C5C64',
  chipText: '#E0E4ED',
  chipBorder: '#9AC0CD',
  listItemBackground: 'rgba(0, 0, 0, 0.5)',
  listItemTextPrimary: '#E0E4ED',
  listItemTextSecondaryVerified: 'green',
  listItemTextSecondaryVerifying: 'orange',
  listItemTextSecondaryFailed: 'red',
  listItemTextSecondaryDefault: '#C2CCDF',
  infoIconColor: '#C2CCDF',
};

// Styled Components
const StyledButton = styled(Button)(({ theme }) => ({
  background: COLORS.buttonGradient,
  color: '#FFFFFF',
  borderRadius: '20px',
  boxShadow: COLORS.buttonBoxShadow,
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: COLORS.buttonHoverBackground,
    boxShadow: COLORS.buttonHoverBoxShadow,
  },
  '&:active': {
    backgroundColor: COLORS.buttonActiveBackground,
  },
}));

const StyledChip = styled('div')(({ theme }) => ({
  backgroundColor: COLORS.chipBackground,
  color: COLORS.chipText,
  borderColor: COLORS.chipBorder,
  borderRadius: '4px',
  padding: '2px 6px',
  margin: '2px',
}));


const WalletManagement = ({ wallets, onAddWallet, onRemoveWallet }) => {
  const { publicKey, connect, disconnect, connected } = useWallet();
  const { discordUserId, guildId } = useContext(AuthContext);
  const [open, setOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [loading, setLoading] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleAddWalletClick = async () => {
    if (!connected) {
      setSnackbar({
        open: true,
        message: 'No wallet connected. Please select and connect a wallet.',
        severity: 'warning',
      });
      return;
    }

    setLoading(true);

    try {
      if (!connected) {
        await connect();
      }

      if (publicKey) {
        const walletAddress = publicKey.toString();

        // Prevent adding duplicate wallets
        if (wallets.some((wallet) => wallet.address === walletAddress)) {
          setSnackbar({
            open: true,
            message: 'Wallet already added.',
            severity: 'warning',
          });
          await disconnect();
          setOpen(false);
          setLoading(false);
          return;
        }

        // Send POST request to backend to add wallet
        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/wallets`,
          { walletAddress: walletAddress.trim() },
          {
            headers: {
              'x-discord-user-id': discordUserId,
              'x-guild-id': guildId,
            },
          }
        );

        if (response.status === 201) { // Adjusted to 201
          onAddWallet(response.data.wallet); // Ensure backend returns { wallet: { ... } }
          setSnackbar({
            open: true,
            message: 'Wallet added successfully!',
            severity: 'success',
          });
          await disconnect(); // Disconnect to allow adding another wallet
          setOpen(false);
        } else {
          setSnackbar({
            open: true,
            message: 'Failed to add wallet.',
            severity: 'error',
          });
        }
      } else {
        setSnackbar({
          open: true,
          message: 'Failed to retrieve wallet address.',
          severity: 'error',
        });
      }
    } catch (error) {
      console.error('Error adding wallet:', error.response?.data?.error || error.message);
      setSnackbar({
        open: true,
        message: `Error: ${error.response?.data?.error || 'Failed to add wallet.'}`,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveWallet = async (walletAddress) => {
    try {
      // Send DELETE request to backend to remove wallet
      const response = await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/wallets/${walletAddress}`,
        {
          headers: {
            'x-discord-user-id': discordUserId,
            'x-guild-id': guildId,
          },
        }
      );

      if (response.status === 200) {
        onRemoveWallet(walletAddress);
        setSnackbar({
          open: true,
          message: 'Wallet removed successfully!',
          severity: 'info',
        });
      } else {
        setSnackbar({
          open: true,
          message: 'Failed to remove wallet.',
          severity: 'error',
        });
      }
    } catch (error) {
      console.error('Error removing wallet:', error.response?.data?.error || error.message);
      setSnackbar({
        open: true,
        message: `Error: ${error.response?.data?.error || 'Failed to remove wallet.'}`,
        severity: 'error',
      });
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar({ ...snackbar, open: false });
  };

  const handleInfoOpen = () => setInfoOpen(true);
  const handleInfoClose = () => setInfoOpen(false);

  return (
    <Box mt={4} width="100%">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6" sx={{ color: COLORS.textPrimary }}>
          Your Wallets
        </Typography>
        <Box>
          <IconButton onClick={handleInfoOpen} sx={{ color: COLORS.infoIconColor }}>
            <InfoOutlined />
          </IconButton>
          <StyledButton variant="contained" startIcon={<Add />} onClick={handleOpen}>
            Add Wallet
          </StyledButton>
        </Box>
      </Box>
      <List>
        {wallets.map((wallet) => (
          <ListItem
            key={wallet.address}
            sx={{
              backgroundColor: COLORS.listItemBackground,
              mb: 1,
              borderRadius: '8px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
            }}
          >
            <ListItemText
              primary={wallet.address}
              secondary={
                wallet.verificationStatus === 'verified'
                  ? 'Verified'
                  : wallet.verificationStatus === 'verifying'
                  ? 'Verifying...'
                  : wallet.verificationStatus === 'failed'
                  ? `Failed: ${wallet.reason}`
                  : 'Unverified'
              }
              primaryTypographyProps={{ sx: { color: COLORS.textPrimary, fontWeight: 600 } }}
              secondaryTypographyProps={{
                sx:
                  wallet.verificationStatus === 'verified'
                    ? { color: COLORS.listItemTextSecondaryVerified }
                    : wallet.verificationStatus === 'verifying'
                    ? { color: COLORS.listItemTextSecondaryVerifying }
                    : wallet.verificationStatus === 'failed'
                    ? { color: COLORS.listItemTextSecondaryFailed }
                    : { color: COLORS.listItemTextSecondaryDefault },
              }}
            />
            <Box display="flex" alignItems="center" mt={1} width="100%">
              {wallet.assignedRoles && wallet.assignedRoles.length > 0 && (
                <Box display="flex" flexWrap="wrap" gap={0.5} flexGrow={1}>
                  {wallet.assignedRoles.map((roleName, index) => (
                    <StyledChip key={`${wallet.address}-${index}`}>
                      {roleName}
                    </StyledChip>
                  ))}
                </Box>
              )}
              <ListItemSecondaryAction>
                <IconButton edge="end" onClick={() => handleRemoveWallet(wallet.address)}>
                  <Delete sx={{ color: COLORS.textPrimary }} />
                </IconButton>
              </ListItemSecondaryAction>
            </Box>
          </ListItem>
        ))}
        {wallets.length === 0 && (
          <Typography variant="body2" sx={{ color: COLORS.textSecondary, textAlign: 'center', mt: 2 }}>
            No wallets added yet.
          </Typography>
        )}
      </List>

      {/* Add Wallet Dialog */}
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle sx={{ color: COLORS.textPrimary }}>Add New Wallet</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: COLORS.textSecondary, mb: 2 }}>
            Connecting your wallet is a <strong>read-only</strong> process used solely for verifying your NFT ownership.
            <br />
            <strong>No transactions</strong> will be made during this process.
          </Typography>
          <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>
            Click the "Connect Wallet" button below to proceed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} sx={{ color: COLORS.textSecondary }}>
            Cancel
          </Button>
          <StyledButton onClick={handleAddWalletClick} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Connect Wallet'}
          </StyledButton>
        </DialogActions>
      </Dialog>

      {/* Information Dialog */}
      <Dialog open={infoOpen} onClose={handleInfoClose}>
        <DialogTitle sx={{ color: COLORS.textPrimary }}>Wallet Connection Information</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>
            This application only requires a read-only connection to your wallet to verify NFT ownership.
            <br />
            <strong>No transactions</strong> will be initiated, and your funds remain secure.
            <br />
            Ensure you are adding a wallet that holds the NFTs you wish to verify.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleInfoClose} sx={{ color: COLORS.textSecondary }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WalletManagement;

